import React, { useState, useEffect } from 'react';
import { Button } from "components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card"
import { Input } from "components/ui/input"
import { Label } from "components/ui/label"
import { auth } from 'firebase-config';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Building2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from 'components/ui/alert';
import { AlertCircle } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Redirect if user is already logged in
  useEffect(() => {
    if (user) {
      setLoginSuccess(true);
      navigate('/');
    }
  }, [user, navigate]);

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Per favore inserisci tutti i campi');
      return;
    }

    setError('');
    setLoading(true);
    
    try {
      await signInWithEmailAndPassword(auth, email, password);
      // Show immediate success feedback
      setLoginSuccess(true);
      // Navigation will be handled by useEffect when user state changes
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setLoading(false);
        setError('Email o password non validi');
      } else if (err.code === 'auth/too-many-requests') {
        setLoading(false);
        setError('Troppi tentativi. Riprova dopo.');
      } else {
        setLoading(false);
        setError('Accesso fallito. Riprova.');
      }
    } 
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Show loading overlay when authenticating or after successful login
  const showLoadingOverlay = loading || (loginSuccess && authLoading);
  
  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-4 relative">
      {/* Full-screen loading overlay */}
      {showLoadingOverlay && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-lg font-medium text-green-600">
              {loginSuccess ? 'Accesso effettuato! Reindirizzamento…' : 'Signing you in...'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Attendere un momento
            </p>
          </div>
        </div>
      )}
      
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <Link to="/" className="flex items-center gap-2 justify-center mb-4">
            <Building2 className="w-8 h-8 text-primary" />
            <span className="text-2xl font-bold text-gray-900">ReCondo</span>
          </Link>
          <CardTitle className="text-2xl">Accedi al tuo account</CardTitle>
          <CardDescription>Inserisci le tue credenziali per accedere</CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>
            
            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="ml-auto inline-block text-sm underline"
                >
                  Password dimenticata?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Login Failed</AlertTitle>
                <AlertDescription>
                  {error}
                </AlertDescription>
              </Alert>
            )}
            
            <Button type="submit" className="w-full" onClick={handleLogin} disabled={loading || loginSuccess}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loginSuccess ? 'Success!' : 'Accedi'}
            </Button>
          </div>
          
          <div className="mt-4 text-center text-sm">
              Non hai ancora un account?{" "}
            <Link to="/signup" className="underline">
              Registrati Ora
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 